import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import CardSeplag from '../../components/cardSeplag/CardSeplag';
import TableSeplag from '../../components/tableSeplag/TableSeplag';
import { errosTratados } from '../../config/Axios';
import { exportar } from '../utils/Exporter';
import { bindParams, printToast } from '../utils/Global';
import { Pagination } from '../utils/Pagination';
import BasePageConfig from './BasePageConfig';

function BasePageLayout({ children, ...rest }) {
    return <CardSeplag {...rest}>{children}</CardSeplag>;
}

function BasePage(
    {
        service,
        page,
        enableVisualizar = true,
        enableEventoAcao = true,
        enableAdicionar = true,
        enableAlterar = true,
        enableExcluir = true,
        enableEditar = true,
        enableExport = true,
        enableSwitch = true,
        lockButtonsRules,
        addBeforeActions = () => <></>,
        addAfterActions = () => <></>,
        addAditionalActions = () => <></>,
        addAfterTable = () => <></>,
        className = '',
        onFind,
        onSetList,
        onExport,
        onDelete,
        filterOnActive = false,
        children,
        LayoutDefault = BasePageLayout,
        initialValues,
        labelIncluir = 'Incluir',
        customExportColumns,
    },
    ref
) {
    let history = useHistory();

    const config = new BasePageConfig(page, initialValues, customExportColumns);

    const {
        configGeral = {},
        configExportar = {},
        configAlterar = {},
        configExcluir = {},
        columnsTable,
    } = config ? config.getConfigs() : {};

    const params = useParams();

    const [Lista, setLista] = useState([]);

    const [totalRecords, setTotalRecords] = useState(0);

    const [pagination, setPagination] = useState(new Pagination());

    useEffect(() => {
        if (filterOnActive) {
            onFiltro();
        }
    }, []);

    const onAdicionar = () => {
        let url = `${config.getBasePage(params)}/new`;
        if (page?.pageUrls?.new) {
            url = bindParams(page?.pageUrls?.new, params);
        }
        history.push(url);
    };

    const onEditar = (data) => {
        let url = `${config.getBasePage(params)}/edit/${data.id}`;
        if (page?.pageUrls?.edit) {
            url = bindParams(page?.pageUrls?.edit, params).replace(':id', data.id);
        }
        history.push(url);
    };

    const onVisualizar = (data) => {
        let url = `${config.getBasePage(params)}/edit/${data.id}/view`;
        if (page?.pageUrls?.view) {
            url = bindParams(page?.pageUrls?.view, params).replace(':id', data.id);
        }
        history.push(url);
    };

    const onExcluir = async (data) => {
        try {
            await (onDelete ? onDelete(data) : service.remover(data));
            onPesquisar(pagination);
            printToast({
                severity: 'success',
                summary: configGeral.pageName,
                detail: configExcluir.messages.success,
            });
        } catch (error) {
            if (!errosTratados.includes(error?.response?.status)) {
                printToast({
                    severity: 'error',
                    summary: configGeral.pageName,
                    detail: configExcluir.messages.error,
                });
            }
        }
    };

    const onAlterarStatus = (data, novoStatus) => {
        service
            .alterarStatus(data, novoStatus)
            .then(() => {
                onPesquisar(pagination);
                printToast({
                    severity: 'success',
                    summary: configGeral.pageName,
                    detail: configAlterar.messages.success,
                });
            })
            .catch(() => {
                printToast({
                    severity: 'error',
                    summary: configGeral.pageName,
                    detail: configAlterar.messages.error,
                });
            });
    };

    const onPesquisar = async (pagination) => {
        pagination.paramsPage = params;
        try {
            const response = await (onFind ? onFind(pagination) : service.pesquisar(pagination));
            const total = response?.totalElements;
            const res = await (onSetList
                ? onSetList(response.content ?? response, config)
                : service.beforeSetList(response.content ?? response, config));
            setLista(res);
            setTotalRecords(total);
        } catch (error) {
            if (!errosTratados.includes(error?.response?.status)) {
                printToast({
                    severity: 'error',
                    summary: configGeral.pageName,
                    detail: error?.message ?? error.toString(),
                });
            }
        }
    };

    const onPaginacao = (event) => {
        pagination.paginate(event);
        setPagination(pagination);
        onPesquisar(pagination);
    };

    const onFiltro = (filtros) => {
        pagination.filters = filtros;
        setPagination(pagination);
        onPesquisar(pagination);
    };

    const onExportar = async (tipo) => {
        try {
            const arquivo = await (onExport
                ? onExport(
                      params,
                      tipo,
                      configExportar.title,
                      pagination.filters,
                      configExportar.config
                  )
                : service.exportaDataToFile(
                      params,
                      tipo,
                      configExportar.title,
                      pagination.filters,
                      configExportar.config
                  ));

            exportar(arquivo, tipo, configExportar.title);
            printToast({
                severity: 'success',
                summary: configGeral.pageName,
                detail: configExportar.messages.success,
            });
        } catch (error) {
            printToast({
                severity: 'error',
                summary: configGeral.pageName,
                detail: configExportar.messages.error,
            });
        }
    };

    useImperativeHandle(
        ref,
        () => {
            return { onFiltro, onAlterarStatus, onExcluir, Lista, setLista, config };
        },
        [Lista, config]
    );

    const childrens = children?.length > 1 ? children : [children];
    return (
        config && (
            <LayoutDefault
                className={className}
                legenda={() => configGeral.legenda}
                lockButtonsRules={lockButtonsRules}
                title={configGeral.pageName}
                config={config}
            >
                {childrens.map((child, index) =>
                    React.cloneElement(child, {
                        key: index,
                        ...child?.props,
                        onFiltro,
                        listaSeplag: Lista,
                    })
                )}

                <TableSeplag
                    hasEventoAcao={enableEventoAcao}
                    onModalSearch
                    onAdicionarDado={enableAdicionar ? onAdicionar : undefined}
                    onVisualizar={enableVisualizar ? onVisualizar : undefined}
                    onExportar={enableExport ? onExportar : undefined}
                    camposTabela={columnsTable}
                    dados={Array.isArray(Lista) ? Lista : Lista?.list}
                    config={config}
                    enableSwitch={enableSwitch}
                    addBeforeActions={addBeforeActions}
                    addAfterActions={addAfterActions}
                    addAditionalActions={addAditionalActions}
                    lockButtonsRules={lockButtonsRules}
                    totalRecords={totalRecords}
                    onEditarDado={enableEditar ? onEditar : undefined}
                    onAlterarStatus={enableAlterar ? onAlterarStatus : undefined}
                    onExcluirDado={enableExcluir ? onExcluir : undefined}
                    onPage={onPaginacao}
                    labelIncluir={labelIncluir}
                />
                {addAfterTable()}
            </LayoutDefault>
        )
    );
}

export default forwardRef(BasePage);
